import { useCallback, useEffect, useState } from "react";
import { DemoNote } from "@/components/DemoNote";
import { Link, useParams } from "react-router-dom";
import { Share2 } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { shareLink } from "@/lib/share";
import { BeforeAfter } from "@/components/BeforeAfter";
import { SiteFooter } from "@/components/Chrome";
import { Attribution } from "@/components/Provenance";
import { Benefits, Scope } from "@/components/proposal/Scope";
import { Support } from "@/components/proposal/Support";
import { Priorities } from "@/components/proposal/Priorities";
import { Package } from "@/components/proposal/Package";
import { OwnerPanel } from "@/components/proposal/OwnerPanel";
import { Compare } from "@/components/proposal/Compare";
import { ShopTheVision } from "@/components/vision/Shop";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

const Chip = ({ children, testid }) => <span className="rounded-full border border-[var(--line)] bg-white/60 px-3 py-1 font-mono-ui text-[0.65rem] uppercase tracking-[0.08em]" data-testid={testid}>{children}</span>;

export default function Proposal() {
  const { id } = useParams();
  const auth = useAuth();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true)), [id]);
  useEffect(() => { load(); }, [load, auth?.user?.id]);

  if (missing) return <main className="mx-auto max-w-3xl px-5 py-28" data-testid="proposal-missing"><p className="font-display text-4xl">This proposal isn't available.</p></main>;
  if (!p) return <main className="mx-auto max-w-6xl px-5 py-16"><div className="aspect-[3/2] rounded bg-[var(--bone-2)] shimmer" /></main>;

  return (
    <main data-testid="proposal-page">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <div className="eyebrow mb-2">PlaceBacker It · {p.propertyTitle}</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="max-w-3xl font-display text-4xl leading-[1.05] tracking-tight sm:text-5xl" data-testid="proposal-title">A project worth backing</h1>
          <button className="btn-primary" onClick={() => shareLink(`${window.location.origin}/m/${p.id}`, "Help make this real")} data-testid="share-proposal-button"><Share2 size={15} /> Share</button>
        </div>
        <DemoNote show={p.demoScenario} />
        <div className="mt-4 flex flex-wrap gap-2">
          <Chip testid="proposer-label">{p.proposer.label} · {p.proposer.name}</Chip>
          <Chip testid="owner-status-chip">{p.ownerStatus}</Chip>
          <Chip testid="funding-status-chip">{p.fundingStatus}</Chip>
        </div>
        <div className="mt-8">
          {p.visionUrl && <BeforeAfter before={assetUrl(p.originalUrl)} after={assetUrl(p.visionUrl)} width={p.width} height={p.height} />}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Attribution provenance={p.attribution} testid="proposal-photo-attribution" />
            <Link to={`/v/${p.visionId}`} className="btn-quiet" data-testid="view-vision-link">View the vision</Link>
          </div>
        </div>
        <div className="mt-14 grid gap-12 lg:grid-cols-[3fr_2fr]">
          <div className="grid content-start gap-14">
            <Scope p={p} />
            <Benefits p={p} onChange={setP} />
            <Priorities p={p} reload={load} />
          </div>
          <div className="grid content-start gap-6 lg:sticky lg:top-20">
            <Support key={`${auth?.user?.id}-${p.support.mine?.amount}`} p={p} onChange={setP} />
            <OwnerPanel p={p} onChange={setP} />
          </div>
        </div>
        <div className="mt-16"><Compare key={p.id} p={p} /></div>
        {p.visionVersionId && <ShopTheVision visionId={p.visionId} versionId={p.visionVersionId} testid="proposal-shop-section" />}
        <div className="mt-16"><Package pkg={p.package} onRetry={() => api.post(`/proposals/${p.id}/package`).then((r) => setP(r.data)).catch((e) => toast(errMsg(e, "Package didn't finish.")))} /></div>
      </div>
      <SiteFooter />
    </main>
  );
}
